/**
 * ProvenanceEvidencePage — Canonical full evidence page for provenance chains.
 *
 * Deep-linkable, shareable, exportable, printable audit view.
 * Renders the full seven-band chain with server verification,
 * receipts, proof bundle, and export capabilities.
 *
 * Route: /access-governance/provenance/actions/:actionId
 */

import type { ActionProvenanceChain, ProvenanceStatus } from './provenance-types';
import { PROVENANCE_COPY, provenanceStatusSeverity, resolveProvenanceStatus } from './provenance-types';
import { ProvenanceShield } from './ProvenanceShield';
import { ProvenanceDrawer } from './ProvenanceDrawer';
import { useProvenanceChain } from '../hooks/useProvenanceChain';
import { useProvenanceExportPermission } from '../hooks/useProvenanceExportPermission';
import { csrfHeaders } from '../utils/csrf';

const React = (globalThis as any).React;
const { useState, useCallback, useEffect } = React;

export interface ProvenanceEvidencePageProps {
  actionId: string;
  onBack?: () => void;
}

const STATUS_ICONS: Record<ProvenanceStatus, string> = {
  verified: '\u2705',
  partial: '\u26A0\uFE0F',
  pending: '\u23F3',
  missing: '\u2753',
  failed: '\u274C',
  restricted: '\uD83D\uDD12',
  not_applicable: '\u2796',
};

export function ProvenanceEvidencePage({ actionId, onBack }: ProvenanceEvidencePageProps) {
  const { chain, status: loadStatus, error, refetch } = useProvenanceChain(actionId);
  const { permission: exportPermission } = useProvenanceExportPermission();

  const [exportError, setExportError] = useState(null);

  const handleExportJson = useCallback(async () => {
    if (!chain || exportPermission !== 'full_audit') return;
    setExportError(null);

    try {
      const response = await globalThis.fetch('/api/v1/analytics/provenance/export', {
        method: 'POST',
        credentials: 'include' as RequestCredentials,
        headers: { 'Content-Type': 'application/json', ...csrfHeaders() },
        body: JSON.stringify({ action_id: actionId }),
      });

      if (!response.ok) {
        setExportError(`Export failed (HTTP ${response.status}). The server must generate audited exports — client-side export is not permitted.`);
        return;
      }

      const exportData = await response.json();
      const blob = new Blob(
        [JSON.stringify(exportData, null, 2)],
        { type: 'application/json' },
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `provenance-${actionId}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setExportError('Unable to reach the export service. Exports must be server-generated for audit compliance.');
    }
  }, [chain, actionId, exportPermission]);

  const handlePrint = useCallback(() => {
    globalThis.print?.();
  }, []);

  if (loadStatus === 'loading') {
    return React.createElement('div', {
      'data-testid': 'evidence-page-loading',
      style: { padding: '48px', textAlign: 'center' },
    },
      React.createElement('div', { style: { fontSize: '14px', color: 'var(--main-text-secondary)' } },
        'Loading provenance chain...',
      ),
    );
  }

  if (loadStatus === 'error' || !chain) {
    return React.createElement('div', {
      'data-testid': 'evidence-page-error',
      style: { padding: '48px', textAlign: 'center' },
    },
      React.createElement('div', { style: { fontSize: '32px', marginBottom: '12px' } }, '\u26A0\uFE0F'),
      React.createElement('div', {
        style: { fontSize: '16px', fontWeight: 600, color: 'var(--main-text)', marginBottom: '8px' },
      }, 'Could not load provenance evidence'),
      React.createElement('div', {
        style: { fontSize: '13px', color: 'var(--main-text-secondary)', marginBottom: '20px' },
      }, error || `No chain found for action ${actionId}`),
      React.createElement('button', {
        onClick: refetch,
        style: {
          padding: '10px 24px', fontSize: '13px', fontWeight: 600,
          borderRadius: '8px', border: 'none', cursor: 'pointer',
          background: 'var(--main-accent, #818cf8)', color: '#fff',
        },
      }, 'Retry'),
    );
  }

  const uiStatus = resolveProvenanceStatus(chain.status, {
    hasActionId: true,
    isToolEvent: true,
    isRestricted: false,
  });
  const copy = PROVENANCE_COPY[uiStatus];
  const severity = provenanceStatusSeverity(uiStatus);

  return React.createElement('div', {
    'data-testid': 'evidence-page',
    style: {
      maxWidth: '960px',
      margin: '0 auto',
      padding: '32px 24px',
    },
  },
    // Header
    React.createElement('div', {
      style: { display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '32px' },
    },
      onBack && React.createElement('button', {
        type: 'button',
        onClick: onBack,
        'aria-label': 'Go back',
        style: {
          border: 'none', background: 'transparent', cursor: 'pointer',
          fontSize: '20px', padding: '4px 8px', color: 'var(--main-text-secondary)',
        },
      }, '\u2190'),
      React.createElement(ProvenanceShield, { bands: undefined, status: chain.status, size: 'lg' }),
      React.createElement('div', { style: { flex: 1 } },
        React.createElement('h1', {
          style: {
            margin: '0 0 4px', fontSize: '20px', fontWeight: 700,
            color: 'var(--main-text)',
          },
        }, copy.drawerTitle),
        React.createElement('div', {
          style: { fontSize: '14px', color: 'var(--main-text-secondary)', marginBottom: '4px' },
        }, chain.verdict.summary),
        React.createElement('div', {
          style: { fontSize: '12px', color: 'var(--main-text-secondary)', opacity: 0.7 },
        }, `Action: ${actionId}`),
        chain.verification.verified_at && React.createElement('div', {
          style: { fontSize: '12px', color: 'var(--main-text-secondary)', opacity: 0.7 },
        }, `Last verified: ${new Date(chain.verification.verified_at).toLocaleString()}`),
      ),
    ),

    // Actions bar
    React.createElement('div', {
      style: { display: 'flex', gap: '12px', marginBottom: exportError ? '8px' : '32px', alignItems: 'center' },
    },
      exportPermission === 'full_audit' && React.createElement('button', {
        type: 'button',
        'data-testid': 'evidence-export-json',
        onClick: handleExportJson,
        style: {
          padding: '8px 16px', fontSize: '13px', fontWeight: 500,
          borderRadius: '6px', border: '1px solid var(--card-border)',
          background: 'transparent', cursor: 'pointer',
          color: 'var(--main-text)',
        },
      }, 'Export JSON'),
      React.createElement('button', {
        type: 'button',
        'data-testid': 'evidence-print',
        onClick: handlePrint,
        style: {
          padding: '8px 16px', fontSize: '13px', fontWeight: 500,
          borderRadius: '6px', border: '1px solid var(--card-border)',
          background: 'transparent', cursor: 'pointer',
          color: 'var(--main-text)',
        },
      }, 'Print'),
    ),
    exportError && React.createElement('div', {
      role: 'alert',
      'data-testid': 'evidence-export-error',
      style: {
        padding: '10px 16px', marginBottom: '32px', fontSize: '13px',
        borderRadius: '6px',
        background: 'color-mix(in srgb, var(--color-danger, #ef4444) 8%, transparent)',
        border: '1px solid color-mix(in srgb, var(--color-danger, #ef4444) 20%, transparent)',
        color: 'var(--color-danger, #ef4444)',
      },
    }, exportError),

    // Full chain detail via ProvenanceDrawer (inline, not in a drawer)
    React.createElement(ProvenanceDrawer, {
      chain,
      exportPermission,
    }),
  );
}

export default ProvenanceEvidencePage;
